import axios from "axios";
import {
  appendTimestamp,
  convertYearToAgeByClient,
  getClientDisplayNameById,
  getSelectedClient,
  getSelectedClientAppliesToType,
  MONTH_LIST,
  MONTH_NUM_MAP,
} from "../../common/shared";
import { SERVER_URL } from "../../config";
import { GOAL_ROW } from "../../redux/constants";

const getGoalTypeName = (goalType) => {
  if (goalType === "RETIREMENT") return "Retirement";
  if (goalType === "LEGACY") return "Legacy";
  if (goalType === "EDUCATION") return "Education";
  if (goalType === "LIFESTYLE") return "Lifestyle";
  if (goalType === "TAX_PLANNING") return "Tax Planning";
};

export const mapBEGoalTOFE = (goal, index) => {
  return {
    id: goal.id,
    rowType: GOAL_ROW,
    name: goal.name,
    editIndex: index,
    goalType: {
      key: goal.type,
      name: getGoalTypeName(goal.type),
    },
    isCopy: false,
    fundingSource: {
      id: goal.fundingSourceId,
      name:
        goal.fundingSourceId === null
          ? "inStream Default"
          : goal.fundingSourceName,
    },
    frequency: { key: goal.amount.frequency, name: "Yearly" },
    futureValue: goal.amount.futureValue,
    appliesTo: {
      id: goal.appliesTo.appliesToId ? goal.appliesTo.appliesToId : 0,
      name: getClientDisplayNameById(goal.appliesTo.appliesToId),
    },
    amount: goal.amount.amount,
    inflationRate: goal.amount.inflationRate,
    startYear: goal.date.startYear,
    startMonth: MONTH_LIST[goal.date.startMonth - 1],
    startAge: convertYearToAgeByClient(
      goal.date.startYear,
      getSelectedClient({
        id: goal.appliesTo.appliesToId ? goal.appliesTo.appliesToId : 0,
      }),
      MONTH_LIST[goal.date.startMonth - 1]
    ),
    endYear: goal.date.endYear,
    endMonth: MONTH_LIST[goal.date.endMonth - 1],
    endAge: convertYearToAgeByClient(
      goal.date.endYear,
      getSelectedClient({
        id: goal.appliesTo.appliesToId ? goal.appliesTo.appliesToId : 0,
      }),
      MONTH_LIST[goal.date.endMonth - 1]
    ),
    start: new Date(
      goal.date.startYear,
      goal.date.startMonth - 1,
      goal.date.startMonth - 1 === 11 ? 31 : 1
    ),
    end: new Date(
      goal.date.endYear,
      goal.date.endMonth - 1,
      goal.date.endMonth - 1 === 11 ? 31 : 1
    ),
    endDateValid: true,
    interval: goal.interval,
    notes: goal.note,
    scopeType: goal.scopeType,
    isEdited: false,
  };
};

export const mapFEGoalToBE = (goal, index) => {
  return {
    id: goal.id,
    editIndex: index,
    name: goal.name,
    type: goal.goalType.key,
    isCopied: goal.isCopy,
    appliesTo: {
      appliesToId: goal.appliesTo.id,
      appliesToType: getSelectedClientAppliesToType(goal.appliesTo),
    },
    amount: {
      frequency: goal.frequency.key,
      amount: goal.amount,
      inflationRate: goal.inflationRate * 100,
      futureValue: goal.futureValue,
    },
    date: {
      startDateType: "REGULAR_DATE",
      endDateType: "REGULAR_DATE",
      relativeStartDateNum: 0,
      relativeEndDateNum: 0,
      startYear: goal.startYear,
      endYear: goal.endYear,
      startMonth: MONTH_NUM_MAP[goal.startMonth],
      endMonth: MONTH_NUM_MAP[goal.endMonth],
    },
    interval: goal.interval,
    fundingSourceId: goal.fundingSource.id,
    fundingSourceName: goal.fundingSource.name,
    note: goal.notes,
    scopeType: goal.scopeType,
    isEdited: goal.isEdited,
  };
};

export const getGoalsList = (advisorId, clientId) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/goals`
    );

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload.map(mapBEGoalTOFE));
    });
  });
};

const postGoal = (advisorId, clientId, goal) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/goal`
    );

    axios.post(targetUrl, goal).then((response) => {
      resolve({});
    });
  });
};

const putGoal = (advisorId, clientId, goal) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/goal/${goal.id}`
    );

    axios.put(targetUrl, goal).then((response) => {
      resolve({});
    });
  });
};

export const saveGoal = (advisorId, clientId, goal) => {
  const mappedGoal = mapFEGoalToBE(goal);

  if (goal.id) {
    return putGoal(advisorId, clientId, mappedGoal);
  }

  return postGoal(advisorId, clientId, mappedGoal);
};

export const deleteGoal = (advisorId, clientId, goalId) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/goal/${goalId}`
    );
    axios.delete(targetUrl).then((response) => resolve({}));
  });
};

export const getFundingSourceList = (advisorId, clientId) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/goal/funding-source/idname`
    );
    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const batchUpdateAdvisorGoalList = (advisorId, clientId, goals) => {
  console.log("Advisor Goal API - batchUpdateGoalList");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/goals`
    );
    const payload = goals.map(mapFEGoalToBE);

    axios.post(targetUrl, payload).then((response) => {
      const result = response.data.payload.map(mapBEGoalTOFE);
      resolve(result);
    });
  });
};

export const batchDeleteAdvisorGoals = (advisorId, clientId, deleteIdList) => {
  console.log("Advisor Goal API - batchDeleteAdvisorGoals");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/goal/batch-delete`
    );

    axios.post(targetUrl, deleteIdList).then(() => resolve({}));
  });
};
